import { create } from 'zustand'
import { createDefaultConfig } from '../data/presets'
import type { MemoryEstimate, SolverConfig, SolverError, SolverProgress, SolverResult } from '../types/solver'

export type Screen = 'home' | 'setup' | 'batch' | 'solving' | 'result' | 'trainer' | 'saved' | 'benchmark' | 'license'
export type ResultTab = 'summary' | 'range' | 'combo' | 'explanation' | 'tree'

interface AppState {
  screen: Screen
  config: SolverConfig
  estimate: MemoryEstimate | null
  progress: SolverProgress | null
  result: SolverResult | null
  error: SolverError | null
  resultTab: ResultTab
  selectedNodeId: string | null
  selectedCombo: string | null
  trainerSourceResultId: string | null
  setScreen: (screen: Screen) => void
  setConfig: (config: SolverConfig) => void
  updateConfig: (updater: (config: SolverConfig) => SolverConfig) => void
  setEstimate: (estimate: MemoryEstimate | null) => void
  setProgress: (progress: SolverProgress | null) => void
  setResult: (result: SolverResult | null) => void
  setError: (error: SolverError | null) => void
  setResultTab: (tab: ResultTab) => void
  setSelectedNode: (id: string | null) => void
  setSelectedCombo: (combo: string | null) => void
  setTrainerSourceResult: (id: string | null) => void
  resetSolveState: () => void
}

export const useAppStore = create<AppState>((set) => ({
  screen: 'home',
  config: createDefaultConfig(),
  estimate: null,
  progress: null,
  result: null,
  error: null,
  resultTab: 'summary',
  selectedNodeId: null,
  selectedCombo: null,
  trainerSourceResultId: null,
  setScreen: (screen) => set({ screen }),
  setConfig: (config) => set({ config, estimate: null }),
  updateConfig: (updater) => set((state) => ({ config: updater(structuredClone(state.config)), estimate: null })),
  setEstimate: (estimate) => set({ estimate }),
  setProgress: (progress) => set({ progress }),
  setResult: (result) => set({
    result,
    selectedNodeId: result?.defaultNodeId ?? null,
    selectedCombo: null,
  }),
  setError: (error) => set({ error }),
  setResultTab: (resultTab) => set({ resultTab }),
  setSelectedNode: (selectedNodeId) => set({ selectedNodeId, selectedCombo: null }),
  setSelectedCombo: (selectedCombo) => set({ selectedCombo }),
  setTrainerSourceResult: (trainerSourceResultId) => set({ trainerSourceResultId }),
  resetSolveState: () => set({ progress: null, result: null, error: null, estimate: null }),
}))
